<template>
  <VueDatePicker
    v-model="computedModel"
    :uid="id || pickerId"
    :locale="locale"
    :enable-time-picker="false"
    :min-date="minDate"
    :max-date="maxDate"
    :auto-apply="true"
    :teleport="teleport"
    :state="!hasError"
    :text-input="textInputOptions"
    v-bind="$attrs"
    placeholder=""
    format="dd/MM/yyyy"
    class="focus-within:ring-2 outline-sky-200 rounded-md relative border-brand-secondary/20 border shadow-lg"
    hide-input-icon
    utc
  >
    <template #dp-input="{ value }">
      <input
        :id="`dp-input-${id || pickerId}`"
        :value="value"
        class="block w-full border-0 p-0 placeholder:text-brand-alternate-20 focus:ring-0 bg-transparent autofill:bg-transparent peer"
        :class="[
          {'text-base md:text-base': size === 'xl'},
          {'text-xs md:leading-normal md:text-sm': size === 'base'},
          { 'px-6 pb-4 pt-6': size === 'xl' },
          { 'px-6 pb-2 pt-3.5': size === 'base' },
        ]"
        placeholder=""
        type="text"
      >
      <label
        :for="`dp-input-${id || pickerId}`"
        class="select-none block text-brand-black/50 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto duration-300 transform z-10 origin-[0] start-6 absolute"
        :class="[
          {'peer-placeholder-shown:scale-100 peer-focus:-translate-y-4 peer-placeholder-shown:translate-y-0 -translate-y-4 top-[1.25rem] scale-90 peer-focus:scale-90': size === 'xl'},
          {'peer-placeholder-shown:scale-95 peer-focus:-translate-y-3 peer-placeholder-shown:translate-y-0 -translate-y-3 top-2 scale-75 peer-focus:scale-75': size === 'base'}
        ]"
      >
        {{ label }}
      </label>
    </template>
  </VueDatePicker>
</template>

<script setup lang="ts">
import { defineModel } from 'vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import type { InputSizes } from '~/types';

defineOptions({
  inheritAttrs: false,
});

interface Props {
  id?: string
  label?: string,
  modelValue?: string | Date | null
  locale?: string
  teleport?: boolean
  minDate?: Date
  maxDate?: Date
  hasError?: boolean
  size?: InputSizes
}

// eslint-disable-next-line no-undef
const pickerId = useId();

withDefaults(defineProps<Props>(), {
  id: undefined,
  label: undefined,
  locale: 'es',
  modelValue: undefined,
  teleport: true,
  minDate: undefined,
  maxDate: undefined,
  hasError: false,
  size: 'xl',
});

const textInputOptions = {
  format: 'dd/MM/yyyy',
};

const computedModel = defineModel<Props['modelValue']>();
</script>

<style>
:root {
  .dp__theme_light {
    --dp-primary-color: theme(colors.brand.primary.DEFAULT);
    --dp-secondary-color: theme(colors.brand.black);
  }
  .dp__cell_disabled {
    --dp-secondary-color: theme(colors.gray.300);
  }
  /* VUE DATEPICKER */
  --dp-border-radius: theme(borderRadius.md);
  --dp-input-padding: 24px 24px 14px 24px;
  --dp-disabled-color-text: #8e8e8e;
  .dp__input {
    @apply border-brand-secondary/20 shadow-lg;
  }
}
</style>
